<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { IslandPublicView } from '~shared/types/island'
  import IslandCard from './IslandCard.vue'
  import IslandFilterBar, { type IslandFilterBarState } from './IslandFilterBar.vue'
  import IslandCardAd from './ads/IslandCardAd.vue'

  const props = defineProps<{
    isLoading?: boolean
    islands: IslandPublicView[]
  }>()

  const emit = defineEmits(['refresh'])

  const filters = ref<IslandFilterBarState>({
    turnips: 'any',
    fruit: 'any',
    hemisphere: 'any',
    visitor: 'any',
    fees: 'any',
    privacy: 'any',
    sort: 'new',
  } satisfies IslandFilterBarState)

  const islandTypeFilter = (island: IslandPublicView) => {
    return filters.value.turnips === 'any' || island.type === filters.value.turnips
  }

  const fruitFilter = (island: IslandPublicView) => {
    return filters.value.fruit === 'any' || island.fruit === filters.value.fruit
  }

  const hemisphereFilter = (island: IslandPublicView) => {
    return filters.value.hemisphere === 'any' || island.hemisphere === filters.value.hemisphere
  }

  const visitorFilter = (island: IslandPublicView) => {
    return (
      filters.value.visitor === 'any' ||
      (filters.value.visitor && island.npc && island.npc.includes(filters.value.visitor))
    )
  }

  const feesFilter = (island: IslandPublicView) => {
    if (filters.value.fees === 'any') return true
    if (filters.value.fees === 'none' && !island.entryFee) return true
    if (!island.entryFee) return false

    return island.entryFee.some(
      ([fee]) =>
        !!fee &&
        ((filters.value.fees === 'bells' && fee.type === 'bells') ||
          (filters.value.fees === 'nmt' && fee.type === 'nmt')),
    )
  }

  const sortIslands = (a: IslandPublicView, b: IslandPublicView) => {
    if (a.listPriority !== b.listPriority) {
      return (b.listPriority ?? 0) - (a.listPriority ?? 0)
    }

    switch (filters.value.sort) {
      case 'new':
        return b.updatedAt.getTime() - a.updatedAt.getTime()

      case 'old':
        return a.updatedAt.getTime() - b.updatedAt.getTime()

      case 'rating':
        const minRatingsRequired = 5
        const siteAverage = 3.0

        const getWeightedScore = (island: IslandPublicView) => {
          const v = island.totalRatings || 0
          const R = island.averageRating || 0

          // Bayesian Average Formula
          return (
            (v / (v + minRatingsRequired)) * R +
            (minRatingsRequired / (v + minRatingsRequired)) * siteAverage
          )
        }

        return getWeightedScore(b) - getWeightedScore(a)

      default:
        return 0
    }
  }

  const privacyFilter = (island: IslandPublicView) => {
    if (island.private === true) return false //should happen server-side, should never be in the results anyways
    if (!island.privacy) return true

    return (
      filters.value.privacy === 'any' ||
      (filters.value.privacy && island.privacy.includes(filters.value.privacy))
    )
  }

  const filteredIslands = computed(() =>
    props.islands
      .filter(
        (island) =>
          islandTypeFilter(island) &&
          fruitFilter(island) &&
          hemisphereFilter(island) &&
          visitorFilter(island) &&
          feesFilter(island) &&
          privacyFilter(island),
      )
      .sort(sortIslands),
  )
</script>

<template>
  <div class="bg-tan-100 min-h-screen py-16 text-black">
    <div class="mx-auto max-w-5xl px-4">
      <!-- Centered Header -->
      <div class="mb-8 flex flex-col items-center gap-6">
        <h2 class="font-display text-5xl font-bold text-black">Open Islands</h2>
        <!-- <button @click="emit('refresh')">Refresh</button> -->

        <!-- Filters Grid/Row -->
        <IslandFilterBar v-model="filters" />
      </div>

      <!-- Cards Grid -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
        <div class="flex items-center justify-center">
          <IslandCardAd />
        </div>
        <IslandCard v-for="(island, idx) in filteredIslands" :key="idx" :island="island" />
      </div>
    </div>
  </div>
</template>
